import api from './api';

/**
 * Operator-Aufrufe des Marktplatzes (Marktplatz-Rework T9): Moderation,
 * plattformweite Bestellungen mit Refund, Abrechnung, Webhook-Events, DATEV.
 * Alle Endpunkte verlangen ROLE_OPERATOR bzw. ROLE_PLATFORM_ADMIN
 * (`/api/platform/marketplace/*`, `/api/platform/billing/*`).
 */

export type ModerationStatus = 'in_review' | 'rejected' | 'published' | 'withdrawn' | 'all';
export type GateCode = 'billing_incomplete' | 'paypal_not_connected';

export interface ModerationListing {
    id: number;
    uuid: string;
    slug: string | null;
    title: string;
    description: string | null;
    thumbnailUrl: string | null;
    status: string;
    visibility: string;
    rejectionReason: string | null;
    companyId: number | null;
    sellerName: string | null;
    category: string | null;
    tags: string[] | null;
    highlights: string[] | null;
    previewVideoUrl: string | null;
    currency: string;
    allowReference: boolean;
    allowCopy: boolean;
    allowSeatCopy: boolean;
    priceReference: string | null;
    priceCopy: string | null;
    priceSeat: string | null;
    platformFeePercent: string;
    paid: boolean;
    submittedAt: string | null;
    createdAt: string | null;
    /** Fehlende Voraussetzungen für die Freigabe (leer = freigabefähig) */
    gate: GateCode[];
    course: {
        id: number;
        title: string;
        description: string | null;
        difficulty: string | null;
        lessonCount: number;
        lessons?: { id: number; title: string; type: string }[];
    } | null;
    /** Alle Kurse des Angebots (Bundle: mehrere); Lektionen je Kurs nur in der Vorschau */
    courses?: { id: number; title: string; lessonCount?: number; duration?: number | null; lessons?: { id: number; title: string; type: string }[] }[];
    isBundle?: boolean;
    courseCount?: number;
}

export interface OperatorOrder {
    uuid: string;
    orderNumber: string;
    channel: 'marketplace' | 'storefront';
    status: string;
    total: string;
    currency: string;
    refundedAmount: string;
    refundableAmount: string;
    refundable: boolean;
    sellerCompanyId: number | null;
    sellerName: string | null;
    buyerCompanyId: number | null;
    buyerName: string | null;
    customerEmail: string | null;
    createdAt: string | null;
    paidAt: string | null;
    items: string[];
}

export interface OperatorStatement {
    id: number;
    number: string;
    companyId: number;
    periodStart: string | null;
    periodEnd: string | null;
    grossTotal: string;
    refundTotal: string;
    platformFeeTotal: string;
    settlementNet: string;
    status: 'open' | 'paid' | 'void';
    settlementMode: 'payout' | 'commission';
    paidAt: string | null;
    paymentReference: string | null;
    documentNumber: string | null;
}

export interface SettlementRunResult {
    month: string;
    results: { companyId: number; statementId: number | null; documentNumber: string | null; error: string | null }[];
}

export interface WebhookEventRow {
    id: number;
    eventId: string;
    eventType: string;
    resourceId: string | null;
    status: string;
    error: string | null;
    receivedAt: string;
    processedAt: string | null;
}

export interface DatevExportRow {
    id: number;
    scope: string;
    companyId: number | null;
    periodStart: string | null;
    periodEnd: string | null;
    bookingCount: number;
    filename: string;
    sha256: string;
    createdAt: string;
}

export interface OperatorReview {
    id: number;
    listingId: number | null;
    listingTitle: string | null;
    reviewerCompanyId: number | null;
    rating: number;
    title: string | null;
    body: string | null;
    sellerReply: string | null;
    status: 'approved' | 'hidden';
    createdAt: string;
}

export const marketplaceOperatorService = {
    // ------------------------------------------------------------ Moderation
    async getModerationQueue(status: ModerationStatus = 'in_review'): Promise<{ listings: ModerationListing[]; pending: number }> {
        const { data } = await api.get('/platform/marketplace/moderation', { params: { status } });
        return { listings: data.listings ?? [], pending: data.pending ?? 0 };
    },
    async getModerationDetail(id: number): Promise<ModerationListing> {
        const { data } = await api.get(`/platform/marketplace/moderation/${id}`);
        return data.listing;
    },
    async approve(id: number): Promise<ModerationListing> {
        const { data } = await api.post(`/platform/marketplace/listings/${id}/approve`);
        return data.listing;
    },
    async reject(id: number, reason: string): Promise<ModerationListing> {
        const { data } = await api.post(`/platform/marketplace/listings/${id}/reject`, { reason });
        return data.listing;
    },

    // ------------------------------------------------- Bestellungen + Refund
    async getOrders(filters: { status?: string; channel?: string; q?: string } = {}): Promise<OperatorOrder[]> {
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => !!v));
        const { data } = await api.get('/platform/marketplace/orders', { params });
        return data.orders ?? [];
    },
    /** Ohne `amount` wird der Restbetrag erstattet. */
    async refund(uuid: string, input: { amount?: string; reason?: string }): Promise<OperatorOrder> {
        const { data } = await api.post(`/platform/marketplace/orders/${uuid}/refund`, input);
        return data.order;
    },

    // --------------------------------------------------------- Abrechnung
    async getStatements(): Promise<OperatorStatement[]> {
        const { data } = await api.get('/platform/billing/statements');
        return data.statements ?? [];
    },
    async runSettlement(month: string, companyId?: number): Promise<SettlementRunResult> {
        const { data } = await api.post('/platform/billing/settlement', { month, ...(companyId ? { companyId } : {}) });
        return data;
    },
    async markStatementPaid(id: number, reference?: string): Promise<void> {
        await api.post(`/platform/marketplace/settlements/${id}/mark-paid`, { reference });
    },

    // ------------------------------------------------------ Webhook-Events
    async getWebhookEvents(status?: string): Promise<WebhookEventRow[]> {
        const { data } = await api.get('/platform/marketplace/webhook-events', { params: status ? { status } : {} });
        return Array.isArray(data) ? data : [];
    },
    async replayWebhookEvent(id: number, force = false): Promise<string> {
        const { data } = await api.post(`/platform/marketplace/webhook-events/${id}/replay`, { force });
        return data.result;
    },

    // --------------------------------------------------------------- DATEV
    async getDatevExports(): Promise<DatevExportRow[]> {
        const { data } = await api.get('/platform/billing/datev/exports');
        return data.exports ?? [];
    },
    /** Erzeugt den Export (Plattform-Sicht) und liefert die CSV als Blob. */
    async createDatevExport(from: string, to: string): Promise<{ blob: Blob; filename: string; bookingCount: number }> {
        const response = await api.post<Blob>('/platform/billing/datev/export', null, {
            params: { from, to, scope: 'platform' },
            responseType: 'blob',
        });
        const disposition = String(response.headers?.['content-disposition'] ?? '');
        const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? `datev-${from}-${to}.csv`;
        return { blob: response.data, filename, bookingCount: Number(response.headers?.['x-booking-count'] ?? 0) };
    },
    async downloadDatevExport(id: number): Promise<Blob> {
        const response = await api.get<Blob>(`/platform/billing/datev/exports/${id}/download`, { responseType: 'blob' });
        return response.data;
    },

    // ----------------------------------------------------------- Bewertungen
    async getReviews(status?: 'approved' | 'hidden'): Promise<OperatorReview[]> {
        const { data } = await api.get('/platform/marketplace/reviews', { params: status ? { status } : {} });
        return data.reviews ?? [];
    },
    async setReviewHidden(id: number, hidden: boolean): Promise<void> {
        await api.post(`/platform/marketplace/reviews/${id}/${hidden ? 'hide' : 'unhide'}`);
    },
};

/** Fehlercodes des Freigabe-Gates (422) aus der Antwort lesen. */
export const gateCodesFromError = (err: any): GateCode[] => err?.response?.data?.gate ?? [];

/** Blob als Datei speichern (Download-Link im Browser). */
export const saveBlob = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

export default marketplaceOperatorService;
